import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
    Avatar,
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    OverlayDrawer,
    Text,
    tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, Navigation24Regular } from '@fluentui/react-icons';
import SidebarNav from './SidebarNav';
import { useIsCompact } from './useMediaQuery';
import { useAuth } from '../auth/AuthContext';

const SIDEBAR_WIDTH = '264px';

const useStyles = makeStyles({
    root: {
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: `${SIDEBAR_WIDTH} minmax(0, 1fr)`,
        backgroundColor: tokens.colorNeutralBackground2,
        color: tokens.colorNeutralForeground1,
        '@media (max-width: 1023px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    sidebar: {
        position: 'sticky',
        top: 0,
        height: '100dvh',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        '@media (max-width: 1023px)': {
            display: 'none',
        },
    },
    main: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    topBar: {
        position: 'sticky',
        top: 0,
        zIndex: 5,
        display: 'none',
        alignItems: 'center',
        gap: '8px',
        height: '56px',
        paddingLeft: '8px',
        paddingRight: '12px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        '@media (max-width: 1023px)': {
            display: 'flex',
        },
    },
    topBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        textDecoration: 'none',
        color: 'inherit',
        flexGrow: 1,
        minWidth: 0,
    },
    topBrandMark: {
        width: '28px',
        height: '28px',
        borderRadius: tokens.borderRadiusCircular,
    },
    topBrandName: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.01em',
    },
    content: {
        width: '100%',
        maxWidth: '1280px',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingTop: '28px',
        paddingBottom: '64px',
        paddingLeft: '32px',
        paddingRight: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        '@media (max-width: 1023px)': {
            paddingTop: '20px',
            paddingLeft: '20px',
            paddingRight: '20px',
        },
        '@media (max-width: 767px)': {
            paddingTop: '16px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '48px',
            gap: '16px',
        },
    },
    drawer: {
        width: `min(${SIDEBAR_WIDTH}, 86vw)`,
    },
    drawerHeader: {
        paddingTop: '8px',
        paddingBottom: 0,
    },
    drawerBody: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    skipLink: {
        position: 'absolute',
        left: '-9999px',
        top: '8px',
        zIndex: 100,
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingRight: '12px',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorBrandForeground1,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow8,
        ':focus': {
            left: '8px',
        },
    },
});

/**
 * Two-pane shell: a fixed navigation rail on wide screens, a top bar with a
 * hamburger + overlay drawer on tablets and phones.
 */
const AppShell: React.FC = () => {
    const styles = useStyles();
    const isCompact = useIsCompact();
    const { user } = useAuth();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    // Route change closes the drawer; leaving compact mode discards it entirely.
    React.useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
    React.useEffect(() => { if (!isCompact) setDrawerOpen(false); }, [isCompact]);

    return (
        <div className={styles.root}>
            <a href="#main-content" className={styles.skipLink}>К содержимому</a>

            <aside className={styles.sidebar}>
                <SidebarNav />
            </aside>

            <div className={styles.main}>
                <header className={styles.topBar}>
                    <Button
                        appearance="subtle"
                        icon={<Navigation24Regular />}
                        aria-label="Открыть меню"
                        aria-expanded={drawerOpen}
                        onClick={() => setDrawerOpen(true)}
                    />
                    <NavLink to="/polls" className={styles.topBrand}>
                        <img src="/favicon.svg" alt="" className={styles.topBrandMark} />
                        <Text className={styles.topBrandName}>Bayer Poll</Text>
                    </NavLink>
                    {user && <Avatar name={user.displayName} size={32} color="colorful" aria-label={user.displayName} />}
                </header>

                <main id="main-content" className={styles.content} tabIndex={-1}>
                    <Outlet />
                </main>
            </div>

            {isCompact && (
                <OverlayDrawer
                    position="start"
                    open={drawerOpen}
                    onOpenChange={(_, data) => setDrawerOpen(data.open)}
                    className={styles.drawer}
                    aria-label="Меню"
                >
                    <DrawerHeader className={styles.drawerHeader}>
                        <DrawerHeaderTitle
                            action={(
                                <Button
                                    appearance="subtle"
                                    aria-label="Закрыть меню"
                                    icon={<Dismiss24Regular />}
                                    onClick={() => setDrawerOpen(false)}
                                />
                            )}
                        >
                            Меню
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody className={styles.drawerBody}>
                        <SidebarNav onNavigate={() => setDrawerOpen(false)} />
                    </DrawerBody>
                </OverlayDrawer>
            )}
        </div>
    );
};

export default AppShell;
